import { useState, useRef, useEffect } from "react";
import "./styles.css";
import { motion } from "motion/react";
import { random, playAudioBeep } from "./utils";

const positions = [
  // Front Left
  {
    name: "Front Left",
    style: {
      top: "calc(env(safe-area-inset-top, 0px) + 20px)",
      left: "calc(env(safe-area-inset-left, 0px) + 20px)",
    },
  },
  // Mid Left
  {
    name: "Mid Left",
    style: {
      top: "calc(var(--top-perc) - (var(--ball-size) / 2))",
      left: "calc(env(safe-area-inset-left, 0px) + 20px)",
    },
  },
  // Back Left
  {
    name: "Back Left",
    style: {
      top: "calc(100% - env(safe-area-inset-bottom, 0px) - var(--ball-size) - 24px)",
      left: "calc(env(safe-area-inset-left, 0px) + 20px)",
    },
  },
  // Front Right
  {
    name: "Front Right",
    style: {
      top: "calc(env(safe-area-inset-top, 0px) + 20px)",
      left: "calc(100% - env(safe-area-inset-right, 0px) - var(--ball-size) - 20px)",
    },
  },
  // Mid Right
  {
    name: "Mid Right",
    style: {
      top: "calc(var(--top-perc) - (var(--ball-size) / 2))",
      left: "calc(100% - env(safe-area-inset-right, 0px) - var(--ball-size) - 20px)",
    },
  },
  // Back Right
  {
    name: "Back Right",
    style: {
      top: "calc(100% - env(safe-area-inset-bottom, 0px) - var(--ball-size) - 24px)",
      left: "calc(100% - env(safe-area-inset-right, 0px) - var(--ball-size) - 20px)",
    },
  },
];

export default function App() {
  const [formData, setFormData] = useState({
    numRallies: 3,
    shotsInRally: 6,
    delayShots: 3,
    delayRallies: 10,
    startAfter: 5,
    sound: true,
  });

  const [drillStatus, setDrillStatus] = useState("idle"); // idle | countdown | rally | rest | complete
  const [currentRally, setCurrentRally] = useState(1);
  const [currentShot, setCurrentShot] = useState(1);
  const [countdownSec, setCountdownSec] = useState(0);
  const [currentPosIndex, setCurrentPosIndex] = useState(1);
  const [ballVisible, setBallVisible] = useState(false);

  const runnerRef = useRef({
    active: false,
    timerId: null,
    intervalId: null,
    lastIndex: -1,
  });

  const stopDrill = () => {
    if (runnerRef.current.timerId) clearTimeout(runnerRef.current.timerId);
    if (runnerRef.current.intervalId) clearInterval(runnerRef.current.intervalId);
    runnerRef.current.active = false;
    runnerRef.current.timerId = null;
    runnerRef.current.intervalId = null;
    setBallVisible(false);
    setDrillStatus("idle");
  };

  useEffect(() => {
    return () => {
      if (runnerRef.current.timerId) clearTimeout(runnerRef.current.timerId);
      if (runnerRef.current.intervalId) clearInterval(runnerRef.current.intervalId);
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const startDrill = (config) => {
    stopDrill();
    runnerRef.current.active = true;
    runnerRef.current.lastIndex = -1;

    const numRallies = Math.max(1, parseInt(config.numRallies, 10) || 1);
    const shotsInRally = Math.max(1, parseInt(config.shotsInRally, 10) || 1);
    const delayShots = Math.max(0.5, parseFloat(config.delayShots) || 3);
    const delayRallies = Math.max(0, parseFloat(config.delayRallies) || 0);
    const startAfter = Math.max(0, parseInt(config.startAfter, 10) || 0);
    const sound = config.sound;

    const cleanConfig = {
      numRallies,
      shotsInRally,
      delayShots,
      delayRallies,
      startAfter,
      sound,
    };

    if (startAfter > 0) {
      setDrillStatus("countdown");
      setCountdownSec(startAfter);
      if (sound) playAudioBeep(600, 0.1);

      let secLeft = startAfter;
      runnerRef.current.intervalId = setInterval(() => {
        if (!runnerRef.current.active) return;
        secLeft -= 1;
        if (secLeft > 0) {
          setCountdownSec(secLeft);
          if (sound) playAudioBeep(600, 0.1);
        } else {
          clearInterval(runnerRef.current.intervalId);
          runnerRef.current.intervalId = null;
          if (sound) playAudioBeep(1200, 0.2);
          runRally(1, cleanConfig);
        }
      }, 1000);
    } else {
      if (sound) playAudioBeep(1200, 0.2);
      runRally(1, cleanConfig);
    }
  };

  const runRally = (rallyNum, config) => {
    if (!runnerRef.current.active) return;

    setDrillStatus("rally");
    setCurrentRally(rallyNum);
    setCurrentShot(1);

    // Pick first corner
    const { index: firstIndex } = random(positions, runnerRef.current.lastIndex);
    runnerRef.current.lastIndex = firstIndex;
    setCurrentPosIndex(firstIndex);
    setBallVisible(true);
    if (config.sound) playAudioBeep(880, 0.12);

    let shotNum = 1;

    const triggerNextShot = () => {
      if (!runnerRef.current.active) return;

      if (shotNum < config.shotsInRally) {
        shotNum += 1;
        setCurrentShot(shotNum);

        const { index: nextIndex } = random(positions, runnerRef.current.lastIndex);
        runnerRef.current.lastIndex = nextIndex;
        setCurrentPosIndex(nextIndex);
        setBallVisible(true);
        if (config.sound) playAudioBeep(880, 0.12);

        runnerRef.current.timerId = setTimeout(triggerNextShot, config.delayShots * 1000);
      } else {
        // Rally completed
        if (rallyNum < config.numRallies) {
          // Rest period before next rally
          setBallVisible(false);
          const restDuration = Math.round(config.delayRallies);

          if (restDuration <= 0) {
            runRally(rallyNum + 1, config);
            return;
          }

          setDrillStatus("rest");
          setCountdownSec(restDuration);
          if (config.sound) playAudioBeep(440, 0.2);

          let restSecondsLeft = restDuration;
          runnerRef.current.intervalId = setInterval(() => {
            if (!runnerRef.current.active) return;
            restSecondsLeft -= 1;
            if (restSecondsLeft > 0) {
              setCountdownSec(restSecondsLeft);
              if (config.sound && restSecondsLeft <= 3) {
                playAudioBeep(600, 0.1);
              }
            } else {
              clearInterval(runnerRef.current.intervalId);
              runnerRef.current.intervalId = null;
              if (config.sound) playAudioBeep(1200, 0.2);
              runRally(rallyNum + 1, config);
            }
          }, 1000);
        } else {
          // Entire drill completed!
          setBallVisible(false);
          setDrillStatus("complete");
          runnerRef.current.active = false;
          if (config.sound) {
            playAudioBeep(523, 0.12);
            setTimeout(() => playAudioBeep(659, 0.12), 120);
            setTimeout(() => playAudioBeep(784, 0.25), 240);
          }
        }
      }
    };

    runnerRef.current.timerId = setTimeout(triggerNextShot, config.delayShots * 1000);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    startDrill(formData);
  };

  return (
    <div className="App">
      {/* Squash Court */}
      <div className="court">
        <div className="service-line"></div>
        <div className="box box-left"></div>
        <div className="box box-right"></div>
        <div className="divider"></div>

        {ballVisible && drillStatus === "rally" && (
          <motion.div
            key={`${currentRally}-${currentShot}`}
            className="ball"
            style={{ ...positions[currentPosIndex].style }}
            initial={{ scale: 0.3, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 500, damping: 25 }}
          >
            <div className="squash-dot"></div>
            <div className="squash-dot"></div>
          </motion.div>
        )}
      </div>

      {/* Live Drill HUD Header */}
      {drillStatus !== "idle" && (
        <header className="drill-hud">
          <div className="hud-badge hud-rally">
            Rally {currentRally}/{formData.numRallies}
          </div>
          {drillStatus === "rally" && (
            <div className="hud-badge hud-shot">
              Shot {currentShot}/{formData.shotsInRally}
            </div>
          )}
          {drillStatus === "countdown" && (
            <div className="hud-badge hud-status">Get Ready</div>
          )}
          {drillStatus === "rest" && (
            <div className="hud-badge hud-status hud-rest-badge">
              Rest ({countdownSec}s)
            </div>
          )}
          <button type="button" className="hud-stop-btn" onClick={stopDrill}>
            Stop
          </button>
        </header>
      )}

      {/* Countdown Overlay */}
      {drillStatus === "countdown" && (
        <div className="court-overlay-msg">
          <div className="overlay-eyebrow">Get Ready</div>
          <div className="overlay-countdown">{countdownSec}</div>
          <div className="overlay-sub">Starting Rally 1 of {formData.numRallies}</div>
        </div>
      )}

      {/* Rest Overlay */}
      {drillStatus === "rest" && (
        <div className="court-overlay-msg rest-msg">
          <div className="overlay-eyebrow">Rest Time</div>
          <div className="overlay-countdown">{countdownSec}s</div>
          <div className="overlay-sub">
            Rally {currentRally} done • Next: Rally {currentRally + 1} of{" "}
            {formData.numRallies}
          </div>
        </div>
      )}

      {/* Drill Complete Overlay */}
      {drillStatus === "complete" && (
        <div className="court-overlay-card">
          <div className="complete-icon">🏆</div>
          <h2>Drill Complete!</h2>
          <p>
            {formData.numRallies} Rallies •{" "}
            {formData.numRallies * formData.shotsInRally} Total Shots
          </p>
          <div className="complete-actions">
            <button
              type="button"
              className="btn-primary"
              onClick={() => startDrill(formData)}
            >
              Drill Again
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setDrillStatus("idle")}
            >
              Change Settings
            </button>
          </div>
        </div>
      )}

      {/* Setup Form Modal */}
      {drillStatus === "idle" && (
        <div className="form-modal-backdrop">
          <form className="drill-form" onSubmit={handleFormSubmit}>
            <div className="form-header">
              <div className="form-icon">⚡</div>
              <h2>Squash Ghosting</h2>
              <p>Set your drill parameters to begin</p>
            </div>

            <div className="form-fields">
              <div className="form-group">
                <label htmlFor="numRallies">Number of rallies:</label>
                <input
                  id="numRallies"
                  name="numRallies"
                  type="number"
                  min="1"
                  max="100"
                  inputMode="numeric"
                  required
                  value={formData.numRallies}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="shotsInRally">Shots in rally:</label>
                <input
                  id="shotsInRally"
                  name="shotsInRally"
                  type="number"
                  min="1"
                  max="200"
                  inputMode="numeric"
                  required
                  value={formData.shotsInRally}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="delayShots">Delay between shots (in a rally):</label>
                <div className="input-with-unit">
                  <input
                    id="delayShots"
                    name="delayShots"
                    type="number"
                    min="0.5"
                    max="60"
                    step="0.5"
                    inputMode="decimal"
                    required
                    value={formData.delayShots}
                    onChange={handleInputChange}
                  />
                  <span className="unit">sec</span>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="delayRallies">
                  Delay between the rallies (in seconds):
                </label>
                <div className="input-with-unit">
                  <input
                    id="delayRallies"
                    name="delayRallies"
                    type="number"
                    min="0"
                    max="300"
                    step="1"
                    inputMode="numeric"
                    required
                    value={formData.delayRallies}
                    onChange={handleInputChange}
                  />
                  <span className="unit">sec</span>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="startAfter">Start after (in seconds):</label>
                <div className="input-with-unit">
                  <input
                    id="startAfter"
                    name="startAfter"
                    type="number"
                    min="0"
                    max="60"
                    step="1"
                    inputMode="numeric"
                    required
                    value={formData.startAfter}
                    onChange={handleInputChange}
                  />
                  <span className="unit">sec</span>
                </div>
              </div>

              <div className="form-group form-group-checkbox">
                <label htmlFor="sound" className="checkbox-label">
                  <input
                    id="sound"
                    name="sound"
                    type="checkbox"
                    checked={formData.sound}
                    onChange={handleInputChange}
                  />
                  <span>Audio beeps during drill</span>
                </label>
              </div>
            </div>

            <button type="submit" className="submit-button">
              Start Drill
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
